import { useState, useEffect, useCallback } from 'react'
import type { FleetLogDlqRow } from '@alpha-edge/shared'
import { Modal, DataTable, colors, btnDanger } from '../../components/ui'
import type { Column } from '../../components/ui'
import { api } from '../../services/api'

// ── helpers ───────────────────────────────────────────────────────────────────

function formatTimestamp(ts: string | null): string {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return ts
  }
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 3) + '...' : s
}

// ── columns ───────────────────────────────────────────────────────────────────

function buildColumns(onDismiss: (id: string) => void): readonly Column<FleetLogDlqRow>[] {
  return [
    {
      key: 'receivedAt',
      header: 'Received',
      render: (r) => formatTimestamp(r.receivedAt),
      sortable: true,
    },
    {
      key: 'sourceEnvironmentId',
      header: 'Env ID',
      render: (r) => (
        <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.sourceEnvironmentId.slice(0, 8)}</span>
      ),
    },
    {
      key: 'rawLine',
      header: 'Raw Line',
      render: (r) => (
        <span title={r.rawLine} style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
          {truncate(r.rawLine, 80)}
        </span>
      ),
    },
    {
      key: 'parseError',
      header: 'Parse Error',
      render: (r) => <span style={{ color: '#dc2626', fontSize: '0.8125rem' }}>{r.parseError}</span>,
    },
    {
      key: 'id',
      header: '',
      render: (r) => (
        <button style={btnDanger} onClick={() => onDismiss(r.id)}>
          Dismiss
        </button>
      ),
    },
  ]
}

// ── component ─────────────────────────────────────────────────────────────────

interface FleetLogDlqModalProps {
  readonly isOpen: boolean
  readonly onClose: () => void
}

export function FleetLogDlqModal({ isOpen, onClose }: FleetLogDlqModalProps) {
  const [rows, setRows] = useState<readonly FleetLogDlqRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await api.get<readonly FleetLogDlqRow[]>('/admin/fleet/logs/dlq?limit=100')
      setRows(result.data ?? [])
      setTotal(result.meta?.total ?? 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load DLQ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      fetchRows()
    }
  }, [isOpen, fetchRows])

  const handleDismiss = useCallback(
    async (id: string) => {
      try {
        await api.delete(`/api/admin/fleet/logs/dlq/${id}`)
        await fetchRows()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to dismiss entry')
      }
    },
    [fetchRows],
  )

  const columns = buildColumns(handleDismiss)

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Quarantined Log Rows (DLQ)" width="900px">
      {error && (
        <div
          style={{
            marginBottom: '16px',
            padding: '10px 14px',
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            color: '#dc2626',
            fontSize: '0.875rem',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: colors.textMuted, fontSize: '0.875rem', padding: '16px 0' }}>Loading...</div>
      ) : (
        <>
          {total > 0 && (
            <p style={{ marginTop: 0, marginBottom: '12px', fontSize: '0.875rem', color: colors.textMuted }}>
              {total.toLocaleString()} {total === 1 ? 'quarantined row' : 'quarantined rows'}
            </p>
          )}
          <DataTable columns={columns} rows={rows} keyField="id" emptyMessage="No quarantined rows. DLQ is empty." />
        </>
      )}
    </Modal>
  )
}
